import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Image, Button, Spinner, Alert, Badge, Row, Col, Modal } from 'react-bootstrap';

const BlogPostPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [hasLiked, setHasLiked] = useState(false);
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Sea Green Color Scheme
  const SEA_GREEN = '#2E8B57';
  const DARK_SEA_GREEN = '#1F6B45';
  const LIGHT_SEA_GREEN = '#3CB371';
  const VERY_LIGHT_SEA_GREEN = '#F0FFF0';
  const SEA_GREEN_TEXT = '#1A3C2E';
  const SEA_GREEN_BORDER = '#8FBC8F';

  const translations = {
    'Back to Blogs': 'العودة إلى المقالات',
    'Published on': 'نشر في',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'Tags': 'الكلمات الدلالية',
    'Author': 'المؤلف',
    'Category': 'الفئة',
    'Error loading post': 'خطأ في تحميل المنشور',
    'Post not found': 'المنشور غير موجود',
    'Like': 'أعجبني',
    'Share': 'مشاركة',
    'Reading Time': 'وقت القراءة',
    'Loading post...': 'جاري تحميل المنشور...',
    'minutes': 'دقائق',
    'You already liked this post': 'لقد أعجبت بهذا المنشور مسبقاً',
    'Thank you for your like!': 'شكراً لإعجابك!',
    'Liked': 'معجب به',
    'Link copied to clipboard!': 'تم نسخ الرابط إلى الحافظة!'
  };

  const translateText = (text) => {
    // Don't translate post content, only translate UI text
    if (text === post?.content) {
      return text;
    }
    return isArabic ? (translations[text] || text) : text;
  };

  // Language-specific fonts
  const fontStyles = {
    arabic: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.8'
    },
    english: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    }
  };

  const currentFontStyle = isArabic ? fontStyles.arabic : fontStyles.english;

  // Check if user has already liked this post
  const checkIfLiked = (postToken) => {
    const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '{}');
    return likedPosts[postToken] === true;
  };

  // Function to increment view count
  const incrementViewCount = async (postId) => {
    try {
      const postRef = doc(db1, 'blog_posts', postId);
      await updateDoc(postRef, {
        views: increment(1),
        lastViewed: serverTimestamp()
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  // Fixed clipboard function
  const copyToClipboard = async (text) => {
    try {
      if (document.activeElement) {
        document.activeElement.blur();
      }
      
      await navigator.clipboard.writeText(text);
      setShowCopiedAlert(true);
      
      setTimeout(() => {
        setShowCopiedAlert(false);
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setShowCopiedAlert(true);
        setTimeout(() => setShowCopiedAlert(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        alert('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!token) {
          setError('No token provided');
          setLoading(false);
          return;
        }

        // Query blog_posts collection for the token
        const postsQuery = query(
          collection(db1, 'blog_posts'),
          where('token', '==', token)
        );

        const querySnapshot = await getDocs(postsQuery);
        
        if (!querySnapshot.empty) {
          const postDoc = querySnapshot.docs[0];
          const postData = postDoc.data();

          const postWithData = {
            id: postDoc.id,
            ...postData,
            createdAt: postData.createdAt?.toDate() || new Date(),
            tags: Array.isArray(postData.tags) ? postData.tags : [],
            views: postData.views || 0,
            likes: postData.likes || 0
          };

          setPost(postWithData);
          setHasLiked(checkIfLiked(token));

          // Increment view count
          await incrementViewCount(postDoc.id);

        } else {
          setError('Post not found'); // Don't translate error messages that come from Firebase
        }

      } catch (err) {
        console.error('Error fetching post:', err);
        setError(`Error loading post: ${err.message}`); // Don't translate error messages
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [token]);

  const handleLike = async () => {
    if (!post) return;

    if (hasLiked) {
      alert(translateText('You already liked this post'));
      return;
    }

    try {
      await updateDoc(doc(db1, 'blog_posts', post.id), {
        likes: increment(1),
        lastLiked: serverTimestamp()
      });

      setPost(prev => ({
        ...prev,
        likes: (prev.likes || 0) + 1
      }));
      setHasLiked(true);

      const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '{}');
      likedPosts[post.token] = true;
      localStorage.setItem('likedPosts', JSON.stringify(likedPosts));

      alert(translateText('Thank you for your like!'));

    } catch (err) {
      console.error('Error updating likes:', err);
    }
  };

  const calculateReadingTime = (content) => {
    if (!content) return 0;
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container className="my-5 text-center" style={currentFontStyle}>
        <Spinner 
          animation="border" 
          style={{ 
            color: SEA_GREEN,
            width: '3rem', 
            height: '3rem' 
          }} 
        />
        <p className="mt-3" style={{ color: SEA_GREEN, fontSize: '1.1rem' }}>
          {translateText('Loading post...')}
        </p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5" style={currentFontStyle}>
        <Alert variant="danger">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/blogs')}
          style={{ 
            backgroundColor: SEA_GREEN, 
            borderColor: SEA_GREEN,
            borderRadius: '8px'
          }}
        >
          {translateText('Back to Blogs')}
        </Button>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container className="my-5" style={currentFontStyle}>
        <Alert variant="warning">
          {translateText('Post not found')}
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/blogs')}
          style={{ 
            backgroundColor: SEA_GREEN, 
            borderColor: SEA_GREEN,
            borderRadius: '8px'
          }}
        >
          {translateText('Back to Blogs')}
        </Button>
      </Container>
    );
  }

  return (
    <Container className="my-5" style={{ 
      maxWidth: '900px',
      direction: isArabic ? 'rtl' : 'ltr',
      ...currentFontStyle
    }}>
      {/* Fullscreen Image Modal */}
      <Modal 
        show={showImageModal} 
        onHide={() => setShowImageModal(false)} 
        size="xl" 
        centered
        style={{ zIndex: 9999 }}
      >
        <Modal.Body className="p-0 bg-dark">
          <div className="position-relative">
            <Image 
              src={post.featuredImage} 
              style={{ 
                width: '100%', 
                height: '90vh', 
                objectFit: 'contain' 
              }}
              alt={post.title}
            />
            <Button 
              variant="light"
              className="position-absolute top-0 end-0 m-3 rounded-circle"
              onClick={() => setShowImageModal(false)}
              style={{
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
                border: 'none'
              }}
            >
              <i className="bi bi-x-lg" style={{ color: SEA_GREEN }}></i>
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Copied Alert */}
      {showCopiedAlert && (
        <Alert 
          variant="success" 
          className="position-fixed top-0 start-50 translate-middle-x mt-3"
          style={{ zIndex: 9999, minWidth: '300px' }}
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-2"></i>
            {translateText('Link copied to clipboard!')}
          </div>
        </Alert>
      )}

      {/* Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button 
          variant="outline-primary"
          onClick={() => navigate('/blogs')}
          style={{
            borderColor: SEA_GREEN,
            color: SEA_GREEN,
            borderRadius: '20px',
            fontWeight: '600'
          }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          {translateText('Back to Blogs')}
        </Button>
        <Button 
          onClick={() => setIsArabic(!isArabic)}
          variant="outline-primary"
          size="sm"
          style={{
            borderColor: SEA_GREEN,
            color: SEA_GREEN,
            borderRadius: '20px',
            fontWeight: '600'
          }}
        >
          {isArabic ? 'English' : 'العربية'}
        </Button>
      </div>

      <Card className="shadow-lg" style={{ 
        border: 'none',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: VERY_LIGHT_SEA_GREEN
      }}>
        {/* Featured Image */}
        {post.featuredImage && (
          <div 
            className="position-relative"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowImageModal(true)}
          >
            <Image 
              src={post.featuredImage} 
              style={{ 
                width: '100%', 
                height: '400px', 
                objectFit: 'cover' 
              }}
              alt={post.title}
              onError={(e) => {
                console.error('Error loading featured image:', post.featuredImage);
                e.target.style.display = 'none';
              }}
            />
            <div 
              className="position-absolute top-50 start-50 translate-middle"
              style={{
                opacity: 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              <i 
                className="bi bi-zoom-in" 
                style={{ 
                  fontSize: '3rem', 
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              ></i>
            </div>
          </div>
        )}

        <Card.Body style={{ padding: '40px' }}>
          {/* Meta Information */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <Badge 
              style={{ 
                backgroundColor: LIGHT_SEA_GREEN, 
                color: 'white',
                fontSize: '0.9rem',
                padding: '8px 16px',
                fontWeight: '600'
              }}
            >
              {post.category || 'Uncategorized'}
            </Badge>
            <div style={{ color: DARK_SEA_GREEN, fontSize: '0.9rem', fontWeight: '600' }}>
              <i className="bi bi-clock me-1"></i>
              {formatDate(post.createdAt)} • 
              {calculateReadingTime(post.content)} {translateText('minutes')}
            </div>
          </div>

          {/* Title */}
          <h1 style={{ 
            color: DARK_SEA_GREEN, 
            fontWeight: '700',
            fontSize: '2.5rem',
            lineHeight: '1.2',
            marginBottom: '1rem'
          }}>
            {post.title || 'Untitled Post'}
          </h1>

          {/* Excerpt */}
          {post.excerpt && (
            <p style={{ 
              color: SEA_GREEN,
              fontSize: '1.2rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              fontStyle: 'italic',
              fontWeight: '500'
            }}>
              {post.excerpt}
            </p>
          )}

          {/* Author and Stats */}
          <Row className="align-items-center mb-4">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  borderRadius: '50%',
                  backgroundColor: SEA_GREEN,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  marginRight: isArabic ? '0' : '15px',
                  marginLeft: isArabic ? '15px' : '0'
                }}>
                  {post.author?.charAt(0) || 'A'}
                </div>
                <div>
                  <div style={{ fontWeight: '700', color: DARK_SEA_GREEN, fontSize: '1.1rem' }}>
                    {post.author || 'Unknown Author'}
                  </div>
                  <small style={{ color: SEA_GREEN, opacity: '0.8', fontWeight: '500' }}>
                    {translateText('Author')}
                  </small>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end gap-4" style={{ color: DARK_SEA_GREEN }}>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {post.views || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Views')}</small>
                </div>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {post.likes || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Likes')}</small>
                </div>
              </div>
            </Col>
          </Row>

          {/* Content */}
          <div style={{ 
            color: SEA_GREEN_TEXT,
            lineHeight: currentFontStyle.lineHeight,
            fontSize: '1.1rem',
            marginBottom: '3rem',
            whiteSpace: 'pre-line',
            fontWeight: '500'
          }}>
            {post.content || (
              <p style={{ color: SEA_GREEN, fontStyle: 'italic', opacity: '0.7' }}>
                No content available for this post.
              </p>
            )}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mb-4">
              <h6 style={{ color: DARK_SEA_GREEN, marginBottom: '0.5rem', fontWeight: '700' }}>
                {translateText('Tags')}:
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <Badge 
                    key={index}
                    style={{ 
                      backgroundColor: 'white',
                      color: SEA_GREEN,
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: `2px solid ${SEA_GREEN_BORDER}`,
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex gap-3 pt-4 border-top" style={{ borderColor: SEA_GREEN_BORDER + '!important' }}>
            <Button 
              variant={hasLiked ? "success" : "outline-primary"}
              onClick={handleLike}
              disabled={hasLiked}
              className="d-flex align-items-center"
              style={{ 
                borderRadius: '8px',
                fontWeight: '600',
                ...(hasLiked ? {
                  backgroundColor: LIGHT_SEA_GREEN,
                  borderColor: LIGHT_SEA_GREEN
                } : {
                  color: SEA_GREEN,
                  borderColor: SEA_GREEN,
                  backgroundColor: 'transparent'
                })
              }}
            >
              <i className={`bi ${hasLiked ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'} me-2`}></i>
              {hasLiked ? translateText('Liked') : translateText('Like')} ({post.likes || 0})
            </Button>
            <Button 
              variant="outline-primary"
              onClick={() => copyToClipboard(window.location.href)}
              className="d-flex align-items-center"
              style={{ 
                borderRadius: '8px',
                color: SEA_GREEN,
                borderColor: SEA_GREEN,
                backgroundColor: 'transparent',
                fontWeight: '600'
              }}
            >
              <i className="bi bi-share me-2"></i>
              {translateText('Share')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BlogPostPage;
